/* oxlint-disable typescript/consistent-type-assertions */
import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import ReactDom from 'react-dom';

import { PopOverPrimitive } from './pop-over.primitive';
import type { PopOverProps } from './pop-over.types';
import { autoPositionByRef } from './utils/auto-position-item';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

const EVENT_LISTENER_OPTIONS =
  parseInt(React.version.split('.')[0]) >= 17 ? { capture: true } : {};

type PopOverRef = HTMLDivElement & {
  reposition: () => void;
};

export const PopOver = React.forwardRef<HTMLDivElement, PopOverProps>(
  (
    {
      alwaysUp = false,
      children,
      className,
      focusTrapProps,
      isInFlow = false,
      isStatic,
      offset,
      onOutsideClick,
      show = false,
      showCloseButton,
      triggerRef,
      ...props
    },
    forwardedRef
  ) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = React.useState({});

    const resolveContainer = React.useCallback(() => {
      if (containerRef.current) return containerRef.current;

      const node = document?.querySelector<HTMLDivElement>(
        '[data-xds="PopOver"]'
      );

      return (containerRef.current = node);
    }, []);

    const reposition = React.useCallback(() => {
      if (show && !isStatic) {
        const container = resolveContainer();
        if (!container) return;

        setPosition(
          autoPositionByRef(
            triggerRef,
            { current: container },
            isInFlow,
            offset,
            alwaysUp
          )
        );
      }
    }, [
      alwaysUp,
      isInFlow,
      isStatic,
      offset,
      resolveContainer,
      show,
      triggerRef,
    ]);

    useIsomorphicLayoutEffect(() => {
      reposition();
    }, [triggerRef, show, reposition]);

    React.useImperativeHandle(forwardedRef, () => {
      const container = resolveContainer();

      if (!container) {
        return null as unknown as PopOverRef;
      }

      return {
        ...container,
        reposition,
      } as PopOverRef;
    }, [reposition, resolveContainer]);

    // Focus management
    React.useEffect(() => {
      if (focusTrapProps?.active === false) return;

      if (show) {
        setTimeout(() => {
          const container = resolveContainer();

          if (container) {
            container.focus();
          }
        }, 50);
      }
    }, [focusTrapProps?.active, resolveContainer, show]);

    // Keyboard handling
    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent): void => {
        if (show && onOutsideClick && event.key === 'Escape') {
          event.preventDefault();
          onOutsideClick();
        }
      };

      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [onOutsideClick, show]);

    useIsomorphicLayoutEffect(() => {
      const checkOutSideClick = (e: MouseEvent): void => {
        const container = resolveContainer();

        if (
          container?.contains(e.target as Node) ||
          triggerRef?.current?.contains(e.target as Node)
        ) {
          return;
        }

        if (onOutsideClick) onOutsideClick();
      };

      if (show) {
        window.addEventListener('resize', reposition, EVENT_LISTENER_OPTIONS);
        document.addEventListener(
          'click',
          checkOutSideClick,
          EVENT_LISTENER_OPTIONS
        );
      }

      return () => {
        window.removeEventListener(
          'resize',
          reposition,
          EVENT_LISTENER_OPTIONS
        );
        document.removeEventListener(
          'click',
          checkOutSideClick,
          EVENT_LISTENER_OPTIONS
        );
      };
    }, [onOutsideClick, reposition, resolveContainer, show, triggerRef]);

    const commonProps = {
      className,
      containerRef,
      onClose: onOutsideClick,
      position,
      role: 'dialog',
      showCloseButton,
    };

    const { focusTrapOptions: userFocusTrapOptions, ...otherFocusTrapProps } =
      focusTrapProps || {};
    const mergedFocusTrapOptions = {
      allowOutsideClick: true,
      clickOutsideDeactivates: Boolean(onOutsideClick),
      escapeDeactivates: Boolean(onOutsideClick),
      fallbackFocus: () => resolveContainer() || document.body,
      returnFocusOnDeactivate: true,
      ...userFocusTrapOptions,
    };

    if (!show) {
      return null;
    }

    if (isInFlow) {
      return (
        <FocusTrap
          active={show}
          {...otherFocusTrapProps}
          focusTrapOptions={mergedFocusTrapOptions}
        >
          <PopOverPrimitive {...commonProps} {...props}>
            {children}
          </PopOverPrimitive>
        </FocusTrap>
      );
    }

    return ReactDom.createPortal(
      <FocusTrap
        active={show}
        {...otherFocusTrapProps}
        focusTrapOptions={mergedFocusTrapOptions}
      >
        <PopOverPrimitive {...commonProps} {...props}>
          {children}
        </PopOverPrimitive>
      </FocusTrap>,
      document.body
    );
  }
);

PopOver.displayName = 'PopOver';
